import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TopNavbar } from "@/components/layout";
import { TaskService } from "@/services/taskService";
import type { Task } from "@/types/task";
import { TbSearch, TbEye, TbCalendar, TbFlag } from "react-icons/tb";

interface AssetSummary {
  asset_id: string;
  asset_field?: string;
  layer_name?: string;
  task_count: number;
  open_tasks: number;
  closed_tasks: number;
  last_activity: string;
  latest_status?: string;
}

export const AssetList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all tasks to extract asset information
  const {
    data: allTasks = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["all-tasks"],
    queryFn: TaskService.getUserTasks,
  });

  // Group tasks by asset_id to create asset summaries
  const assetSummaries = useMemo(() => {
    const assetMap = new Map<string, AssetSummary>();

    allTasks.forEach((task: Task) => {
      if (!task.asset_id) return;

      const existing = assetMap.get(task.asset_id);
      const isOpen = !task.completed_at;

      if (existing) {
        existing.task_count++;
        if (isOpen) existing.open_tasks++;
        else existing.closed_tasks++;

        // Update last activity if this task is more recent
        if (new Date(task.updated_at) > new Date(existing.last_activity)) {
          existing.last_activity = task.updated_at;
          existing.latest_status = task.status?.label;
        }
      } else {
        assetMap.set(task.asset_id, {
          asset_id: task.asset_id,
          asset_field: task.asset_field,
          layer_name: task.layer_name,
          task_count: 1,
          open_tasks: isOpen ? 1 : 0,
          closed_tasks: isOpen ? 0 : 1,
          last_activity: task.updated_at,
          latest_status: task.status?.label,
        });
      }
    });

    return Array.from(assetMap.values()).sort(
      (a, b) =>
        new Date(b.last_activity).getTime() -
        new Date(a.last_activity).getTime()
    );
  }, [allTasks]);

  // Filter assets based on search term
  const filteredAssets = useMemo(() => {
    if (!searchTerm) return assetSummaries;
    return assetSummaries.filter(
      (asset) =>
        asset.asset_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.layer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.asset_field?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [assetSummaries, searchTerm]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets-management/assets_lifecycle/${assetId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavbar />
        <div className="flex justify-center items-center py-20">
          <div className="text-gray-500">Loading assets...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavbar />
        <div className="flex justify-center items-center py-20">
          <div className="text-red-500">Error loading assets</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavbar />

      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Asset Management</h1>
          <div className="text-sm text-gray-600">
            {filteredAssets.length} assets found
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <TbSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Asset Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-500 text-lg">
                {searchTerm
                  ? "No assets found matching your search."
                  : "No assets found."}
              </div>
              <div className="text-gray-400 text-sm mt-2">
                Assets will appear here when tasks are created with asset IDs.
              </div>
            </div>
          ) : (
            filteredAssets.map((asset) => (
              <div
                key={asset.asset_id}
                onClick={() => handleAssetClick(asset.asset_id)}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                {/* Asset Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {asset.asset_id}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {asset.layer_name || "Unknown Layer"}
                    </p>
                  </div>
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                    <TbEye size={20} />
                  </button>
                </div>

                {/* Task Summary */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {asset.task_count}
                    </div>
                    <div className="text-xs text-gray-500">Total Tasks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {asset.open_tasks}
                    </div>
                    <div className="text-xs text-gray-500">Open</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {asset.closed_tasks}
                    </div>
                    <div className="text-xs text-gray-500">Closed</div>
                  </div>
                </div>

                {/* Asset Details */}
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <TbCalendar size={14} className="mr-2" />
                    Last Activity: {formatDate(asset.last_activity)}
                  </div>
                  {asset.latest_status && (
                    <div className="flex items-center text-sm text-gray-600">
                      <TbFlag size={14} className="mr-2" />
                      Status: {asset.latest_status}
                    </div>
                  )}
                  {asset.asset_field && (
                    <div className="text-xs text-gray-500 mt-2">
                      Field: {asset.asset_field}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
